import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, Edit, Send, X, Upload as UploadIcon, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import TrainingProposalForm from '../forms/TrainingProposalForm';
import ProposalUpload from '../forms/ProposalUpload';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ProposalsContracts = () => {
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/proposals/my-proposals`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setDocuments(response.data.proposals || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching proposals:', err);
      setError('Failed to load proposals');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'signed':
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateProposal = () => {
    setShowCreateOptions(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600">Loading proposals...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center justify-center space-y-4">
              <AlertCircle className="w-16 h-16 text-red-500" />
              <div>
                <p className="text-gray-900 font-semibold mb-2">Failed to load proposals</p>
                <p className="text-gray-600 mb-4">{error}</p>
                <Button onClick={fetchProposals} className="bg-green-600 hover:bg-green-700">
                  Try Again
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleCreateDetailed = () => {
    setShowCreateOptions(false);
    setShowProposalForm(true);
  };

  const handleUploadExisting = () => {
    setShowCreateOptions(false);
    setShowUploadForm(true);
  };

  const handleProposalSubmit = (data) => {
    console.log('Proposal submitted:', data);
    alert('Proposal created successfully!');
    setShowProposalForm(false);
  };

  const handleUploadSubmit = (data) => {
    console.log('Proposal uploaded:', data);
    alert('Proposal uploaded successfully!');
    setShowUploadForm(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Proposals & Contracts</span>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleCreateProposal}
            >
              <FileText className="w-4 h-4 mr-2" />
              Create New Proposal
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {documents.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-2">No proposals yet</p>
                <p className="text-gray-500 text-sm mb-4">Create your first proposal to get started</p>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleCreateProposal}
                >
                  Create New Proposal
                </Button>
              </div>
            ) : (
              documents.map((doc) => (
              <Card key={doc.id} className="border-2 hover:border-green-200 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold">{doc.title}</h3>
                        <Badge variant="outline">
                          {doc.type}
                        </Badge>
                        <Badge className={getStatusColor(doc.status)}>
                          {doc.status}
                        </Badge>
                      </div>
                      <p className="text-blue-600 font-medium mb-1">{doc.client}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>Created: {doc.date}</span>
                        {doc.dueDate && (
                          <>
                            <span>•</span>
                            <span>Due: {doc.dueDate}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">{doc.amount}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => window.open(`${API_BASE_URL}/api/proposals/${doc.id}/view`, '_blank')}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            // Navigate to edit page or open edit modal
                            alert(`Edit functionality for proposal: ${doc.title}\nThis will open the proposal editor.`);
                            // TODO: Implement edit functionality
                          }}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const response = await axios.get(
                                `${API_BASE_URL}/api/proposals/${doc.id}/download`,
                                {
                                  headers: { Authorization: `Bearer ${token}` },
                                  responseType: 'blob'
                                }
                              );
                              
                              // Create download link
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `${doc.title}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error('Download error:', error);
                              alert('Download feature is not yet implemented in the backend.');
                            }
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                        {doc.status === 'Draft' && (
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                await axios.put(
                                  `${API_BASE_URL}/api/proposals/${doc.id}/send`,
                                  {},
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                alert('Proposal sent successfully!');
                                fetchProposals();
                              } catch (error) {
                                console.error('Send error:', error);
                                alert('Failed to send proposal. Please try again.');
                              }
                            }}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Send
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Proposal Options Modal */}
      <Dialog open={showCreateOptions} onOpenChange={setShowCreateOptions}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Choose How to Create Your Proposal
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-6 space-y-4">
            {/* Option 1: Create Detailed Proposal */}
            <button
              onClick={handleCreateDetailed}
              className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all group text-left"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Create Detailed Proposal
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Use our comprehensive multi-step form to create a professional proposal with all required sections including trainer information, qualifications, methodology, pricing, and more.
                  </p>
                  <div className="flex items-center text-sm text-green-600 font-medium">
                    <span>Recommended for new proposals</span>
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </button>

            {/* Option 2: Upload Existing Proposal */}
            <button
              onClick={handleUploadExisting}
              className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group text-left"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <UploadIcon className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Upload Existing Proposal
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Already have a proposal document prepared? Upload your existing PDF or Word document to quickly add it to your proposals.
                  </p>
                  <div className="flex items-center text-sm text-blue-600 font-medium">
                    <span>Quick upload option</span>
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </button>
          </div>

          <div className="mt-6 flex justify-end">
            <Button 
              variant="outline" 
              onClick={() => setShowCreateOptions(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detailed Proposal Form Modal */}
      {showProposalForm && (
        <TrainingProposalForm
          opportunity={{ title: 'New Proposal' }}
          onClose={() => setShowProposalForm(false)}
          onSubmit={handleProposalSubmit}
        />
      )}

      {/* Upload Proposal Modal */}
      {showUploadForm && (
        <ProposalUpload
          opportunity={{ title: 'New Proposal' }}
          onClose={() => setShowUploadForm(false)}
          onSubmit={handleUploadSubmit}
        />
      )}
    </div>
  );
};

export default ProposalsContracts;